import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileText,
  Folder,
  FolderOpen,
  UploadSimple,
  MagnifyingGlass,
  Plus,
  CaretRight,
  CaretDown,
  Download,
  Trash,
  GitBranch,
  TreeStructure,
  Buildings,
  Package,
  PencilSimple,
  Eye,
  Copy,
  FolderSimple,
  PaperPlaneTilt,
  ClipboardText,
  ArrowSquareIn,
  ArrowSquareOut,
  SquaresFour,
  Funnel,
  FunnelSimple,
  X
} from '@phosphor-icons/react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
} from '@/components/ui/context-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getLatestRevision, compareRevisions } from '../utils/revisionSort';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default column widths for Documents
const DEFAULT_DOC_COLUMN_WIDTHS = {
  checkbox: 40,
  status: 80,
  document_number: 250,
  title: 280,
  discipline: 90,
  package: 120,
  revision: 60,
  review_status: 90,
  classification: 100,
  revision_date: 100,
  review_code_label: 120,
  last_comment_date: 100,
  outgoing_transmittal_nr: 140,
  last_revision_date: 100,
  actions: 100
};

// Minimum column widths (allows columns to be resized smaller than default)
const MIN_COLUMN_WIDTHS = {
  checkbox: 32,
  document_number: 220,
  title: 80,
  discipline: 40,
  package: 40,
  revision: 30,
  review_status: 40,
  classification: 40,
  review_code_label: 40,
  last_comment_date: 50,
  outgoing_transmittal_nr: 50,
  last_revision_date: 50,
  actions: 50
};

// Column definitions for reorderable table
const ALL_COLUMNS = [
  { id: 'document_number', label: 'Doc Number', sortable: true, filterable: true },
  { id: 'title', label: 'Title', sortable: true, filterable: false },
  { id: 'discipline', label: 'Discipline', sortable: true, filterable: true },
  { id: 'package', label: 'Package', sortable: true, filterable: true },
  { id: 'revision', label: 'Rev', sortable: true, filterable: true },
  { id: 'review_status', label: 'Status', sortable: true, filterable: true },
  { id: 'classification', label: 'Classification', sortable: true, filterable: true },
  { id: 'revision_date', label: 'Revision Date', sortable: true, filterable: false },
  { id: 'review_code_label', label: 'Revision Code', sortable: true, filterable: true },
  { id: 'last_comment_date', label: 'Comment Date', sortable: true, filterable: true },
  { id: 'outgoing_transmittal_nr', label: 'Outgoing Transmittal', sortable: true, filterable: true },
  { id: 'last_revision_date', label: 'Date', sortable: true, filterable: false },
  { id: 'actions', label: 'Actions', sortable: false, filterable: false },
];

const DEFAULT_COLUMN_ORDER = ALL_COLUMNS.map(c => c.id);

// Short date used for date-column filter values (must match the displayed cell text)
const toDateFilterValue = (dateString) => {
  if (!dateString) return '';
  const s = String(dateString);
  const d = new Date(s.endsWith('Z') || s.includes('+') ? s : s + 'Z');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString();
};

// Sort icon component (outside main component to avoid re-creation)
const SortIcon = ({ column, sortColumn, sortDirection }) => {
  if (sortColumn !== column) {
    return <ArrowUpDown className="h-3 w-3 text-gray-400 ml-1" />;
  }
  return sortDirection === 'asc' 
    ? <ArrowUp className="h-3 w-3 text-blue-600 ml-1" />
    : <ArrowDown className="h-3 w-3 text-blue-600 ml-1" />;
};

// v51: Company values are user-entered and inconsistent ("Enerflex" vs "Enerflex ").
// Normalize for grouping/matching; display the richest label we saw.
const normCompany = (v) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
const companyKey = (v) => normCompany(v).toLowerCase();
const dedupeCompanies = (values) => {
  const map = new Map();
  (values || []).forEach((raw) => {
    const label = normCompany(raw);
    if (!label) return;
    const key = label.toLowerCase();
    const existing = map.get(key);
    if (!existing || label.length > existing.length) map.set(key, label);
  });
  return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
};

const Documents = ({ user, selectedProject }) => {
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [searchQuery, setSearchQuery] = useState(() => localStorage.getItem('docSearchQuery') || '');
  const [multiSearchOpen, setMultiSearchOpen] = useState(false);
  const [multiSearchInput, setMultiSearchInput] = useState('');
  const [multiSearchResult, setMultiSearchResult] = useState(null);
  const [multiSearchLoading, setMultiSearchLoading] = useState(false);
  const [bulkTransferOpen, setBulkTransferOpen] = useState(false);
  const [bulkTransferProjectId, setBulkTransferProjectId] = useState('');
  const [bulkTransferSubmitting, setBulkTransferSubmitting] = useState(false);
  const [searchDate, setSearchDate] = useState(() => localStorage.getItem('docSearchDate') || '');

  // Persist search to localStorage
  useEffect(() => {
    localStorage.setItem('docSearchQuery', searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    localStorage.setItem('docSearchDate', searchDate);
  }, [searchDate]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [folderOpen, setFolderOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  
  // Per-column filter state: { columnId: 'selectedValue' }
  const [columnFilters, setColumnFilters] = useState({});
  const [openFilterColumn, setOpenFilterColumn] = useState(null);
  const filterDropdownRef = useRef(null);

  // Create Transmittal state
  const [transmittalOpen, setTransmittalOpen] = useState(false);
  const [transmittalData, setTransmittalData] = useState({
    transmittal_nr: '',
    attn: '',
    purpose: [],
    return_codes: {},
    remarks: {}
  });
  
  // User permissions
  const isSuperAdmin = user?.is_super_admin || user?.role?.toLowerCase() === 'admin';
  const permissions = user?.permissions || {};
  const canEdit = permissions.can_edit_documents || isSuperAdmin;
  const canDownloadPdf = permissions.can_download_pdf || isSuperAdmin;
  const canDownloadNative = permissions.can_download_native || isSuperAdmin;
  // v26b: doc.js permission gating
  const canExportExcel = isSuperAdmin || !!permissions.can_export_excel;
  const canCreateTransmittal = isSuperAdmin || !!permissions.can_create_transmittals;
  
  // Projects state for transfer feature (not for filtering - that comes from global selector)
  const [projects, setProjects] = useState([]);
  
  // Multi-document editor state
  const [multiEditOpen, setMultiEditOpen] = useState(false);
  const [documentsToEdit, setDocumentsToEdit] = useState([]);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState('document_number');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Pagination state
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('docPageSize');
    return saved ? parseInt(saved, 10) : 50;
  });
  const [currentPage, setCurrentPage] = useState(1);
  
  // Resizable columns state
  const [columnWidths, setColumnWidths] = useState(DEFAULT_DOC_COLUMN_WIDTHS);
  const resizingRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Column ordering state (drag-and-drop)
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem('docColumnOrder');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const allIds = ALL_COLUMNS.map(c => c.id);
        const valid = parsed.filter(id => allIds.includes(id));
        const missing = allIds.filter(id => !valid.includes(id));
        return [...valid, ...missing];
      } catch { return DEFAULT_COLUMN_ORDER; }
    }
    return DEFAULT_COLUMN_ORDER;
  });
  const dragColumnRef = useRef(null);
  const navigate = useNavigate();
  
  // Incoming Transmittal modal state
  const [incomingTransmittalOpen, setIncomingTransmittalOpen] = useState(false);
  const [incomingTransmittalData, setIncomingTransmittalData] = useState({
    transmittal_nr: '', sent_date: '', company: ''
  });
  
  // Sidebar tree filters
  const [expandedTrees, setExpandedTrees] = useState({
    documents: true,
    discipline: true,
    package: false,
    company: false
  });
  const [filterPanelWidth, setFilterPanelWidth] = useState(() => {
    const saved = localStorage.getItem('docFilterPanelWidth');
    const parsed = saved ? parseInt(saved, 10) : 256;
    return Number.isFinite(parsed) ? parsed : 256;
  });
  const [treeFilters, setTreeFilters] = useState(() => {
    const saved = localStorage.getItem('docTreeFilters');
    return saved ? JSON.parse(saved) : {
      discipline: null,
      originator: null,
      projectCode: null
    };
  });
  
  // Unique values for tree filters (extracted from documents)
  const [uniqueValues, setUniqueValues] = useState({
    disciplines: [],
    disciplineCodes: [],
    companyCodes: [],
    originators: [],
    projectCodes: []
  });
  
  const [filters, setFilters] = useState({
    discipline: '',
    document_type: '',
    project_code: '',
    priority: '',
    approval_status: ''
  });
  const [uploadData, setUploadData] = useState({
    name: '',
    description: '',
    tags: '',
    // Identification
    document_number: '',
    revision: '',
    revision_date: '',
    company_document_nr: '',
    // Classification
    discipline: '',
    discipline_code: '',
    document_type: '',
    subclass_code: '',
    classification: '',
    // Project
    project_code: '',
    project_title: '',
    work_package_code: '',
    purchase_order: '',
    asset_item: '',
    // People/Company
    originator: '',
    responsible_engineer: '',
    company_code: '',
    // Status
    approval_status: '',
    priority: '',
    current_review_status: '',
    // Dates
    planned_start: '',
    forecast_start: '',
    actual_start: '',
    planned_end: '',
    forecast_end: '',
    // Other
    external_refs: '',
    language: '',
    system_main_division: ''
  });
  const [newFolderName, setNewFolderName] = useState('');
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  
  // Edit document state
  const [editDocOpen, setEditDocOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editDocData, setEditDocData] = useState({});
  
  // Transfer document state (System Admin only)
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [documentToTransfer, setDocumentToTransfer] = useState(null);
  const [targetProjectId, setTargetProjectId] = useState('');

  // Metadata suggestions for autocomplete
  const [metadataSuggestions, setMetadataSuggestions] = useState({});

  // Classification options
  const CLASSIFICATION_OPTIONS = ['IFR', 'IFA', 'IFC', 'IFP', 'IFI', 'IFD', 'AB'];

  // Close column filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openFilterColumn && filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
        setOpenFilterColumn(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openFilterColumn]);

  // Helper to get display value for a column from a doc
  const getColumnValue = useCallback((doc, colId) => {
    switch(colId) {
      case 'document_number': return doc.document_number || '';
      case 'discipline': return doc.discipline || '';
      case 'package': return doc.project_code || '';
      case 'revision': return doc.latest_revision || doc.revision || '';
      case 'review_status': return doc.latest_status || doc.status || 'draft';
      case 'classification': return doc.classification || '';
      case 'review_code_label': return doc.review_code_label || '';
      case 'outgoing_transmittal_nr': return doc.outgoing_transmittal_nr || '';
      case 'last_comment_date': return toDateFilterValue(doc.last_comment_date);
      default: return '';
    }
  }, []);

  // Compute unique values per filterable column
  const columnUniqueValues = useMemo(() => {
    const result = {};
    ALL_COLUMNS.filter(c => c.filterable).forEach(col => {
      const vals = [...new Set(documents.map(d => getColumnValue(d, col.id)).filter(Boolean))];
      if (col.id.endsWith('_date')) {
        vals.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
      } else {
        vals.sort();
      }
      result[col.id] = vals;
    });
    return result;
  }, [documents, getColumnValue]);

  useEffect(() => {
    fetchFolders();
    fetchProjects();
    fetchMetadataSuggestions();
  }, []);
  
  useEffect(() => {
    fetchDocuments();
  }, [currentFolder, selectedProject]);
  
  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const fetchMetadataSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/documents/metadata/suggestions`);
      setMetadataSuggestions(response.data);
    } catch (error) {
      console.error('Failed to fetch metadata suggestions:', error);
    }
  };

  const fetchFolders = async () => {
    try {
      const response = await axios.get(`${API}/folders`);
      setFolders(response.data);
    } catch (error) {
      toast.error('Failed to fetch folders');
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      let url = `${API}/documents`;
      const params = new URLSearchParams();
      if (currentFolder) {
        params.append('folder_id', currentFolder);
      }

      // Load enough documents for the complete general register.
      params.append('limit', '10000');
      // Project filter
      if (selectedProject) {
        params.append('project_id', selectedProject);
      }
      // Only pass search to backend, do filtering client-side
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      const response = await axios.get(url);
      setDocuments(response.data);
      
      // Extract unique values for tree filters from all documents
      const disciplines = [...new Set(response.data.map(d => d.discipline).filter(Boolean))];
      const disciplineCodes = [...new Set(response.data.map(d => d.discipline_code).filter(Boolean))];
      const companyCodes = [...new Set(response.data.map(d => d.company_code).filter(Boolean))];
      const originators = dedupeCompanies(response.data.map(d => d.source_company || d.originator));
      const projectCodes = [...new Set(response.data.map(d => d.project_code).filter(Boolean))];
      
      setUniqueValues({
        disciplines: disciplines.sort(),
        disciplineCodes: disciplineCodes.sort(),
        companyCodes: companyCodes.sort(),
        originators: originators,
        projectCodes: projectCodes.sort()
      });
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else if (error.response?.status === 500) {
        toast.error('Server error. Please try again later.');
      } else if (!error.response) {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to fetch documents.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch transmittals for context menu
  // Context menu handlers
  const handleCopyDocNumber = (doc) => {
    navigator.clipboard.writeText(doc.document_number || doc.id);
    toast.success('Document number copied to clipboard');
  };

  const handleViewRevisions = (doc) => {
    navigate(`/documents/${doc.id}/cycles`);
  };

  const handleStartReview = async (doc) => {
    try {
      // Create a new review cycle
      await axios.post(`${API}/cycles`, {
        document_id: doc.id,
        revision: doc.revision || 'A'
      });
      toast.success('Review cycle created');
      navigate(`/documents/${doc.id}/cycles`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create review cycle');
    }
  };

  const handleContextCreateIncoming = (doc) => {
    setSelectedDocuments(new Set([doc.id]));
    setIncomingTransmittalOpen(true);
  };

  const handleContextCreateOutgoing = (doc) => {
    setSelectedDocuments(new Set([doc.id]));
    setTransmittalOpen(true);
  };

  const handleCreateIncomingTransmittal = async () => {
    if (!incomingTransmittalData.transmittal_nr) {
      toast.error('Please enter a transmittal number');
      return;
    }
    try {
      const res = await axios.post(`${API}/transmittals/incoming-from-documents`, {
        transmittal_nr: incomingTransmittalData.transmittal_nr,
        sent_date: incomingTransmittalData.sent_date || null,
        company: incomingTransmittalData.company || '',
        document_ids: Array.from(selectedDocuments),
      });
      const skipped = res.data?.skipped_document_ids?.length || 0;
      toast.success(`Incoming transmittal created with ${res.data?.document_count ?? selectedDocuments.size} document(s)`);
      if (skipped > 0) toast.error(`${skipped} selected document(s) could not be linked (no longer exist)`);
      setIncomingTransmittalOpen(false);
      setIncomingTransmittalData({ transmittal_nr: '', sent_date: '', company: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create incoming transmittal');
    }
  };

  const handleMoveToFolder = async (doc, folderId) => {
    try {
      await axios.put(`${API}/documents/${doc.id}`, { folder_id: folderId || null });
      toast.success(folderId ? 'Document moved to folder' : 'Document moved to root');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move document');
    }
  };

  const handlePreview = (doc) => {
    // Navigate to document detail page or open preview
    navigate(`/documents/${doc.id}`);
  };

  // Sort handling
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Column resize handlers
  const startResize = useCallback((columnKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = columnKey;
    startXRef.current = e.clientX;
    startWidthRef.current = columnWidths[columnKey] || 150;

    const handleMouseMove = (moveEvent) => {
      if (!resizingRef.current) return;
      const diff = moveEvent.clientX - startXRef.current;
      const minW = MIN_COLUMN_WIDTHS[resizingRef.current] || 40;
      const newWidth = Math.max(minW, startWidthRef.current + diff);
      setColumnWidths(prev => ({ ...prev, [resizingRef.current]: newWidth }));
    };

    const handleMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  // Filter panel resize handler
  const startFilterPanelResize = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = filterPanelWidth;
    const handleMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - startX;
      const newWidth = Math.min(600, Math.max(180, startW + diff));
      setFilterPanelWidth(newWidth);
    };
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setFilterPanelWidth((w) => {
        localStorage.setItem('docFilterPanelWidth', String(w));
        return w;
      });
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [filterPanelWidth]);

  // Render resizable header function
  // Column drag-and-drop handlers
  const handleDragStart = (e, columnId) => {
    dragColumnRef.current = columnId;
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };
  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    dragColumnRef.current = null;
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e, targetId) => {
    e.preventDefault();
    const sourceId = dragColumnRef.current;
    if (!sourceId || sourceId === targetId) return;
    setColumnOrder(prev => {
      const newOrder = [...prev];
      const sourceIdx = newOrder.indexOf(sourceId);
      const targetIdx = newOrder.indexOf(targetId);
      newOrder.splice(sourceIdx, 1);
      newOrder.splice(targetIdx, 0, sourceId);
      localStorage.setItem('docColumnOrder', JSON.stringify(newOrder));
      return newOrder;
    });
  };

  // Ordered columns for rendering
  const orderedColumns = columnOrder
    .map(id => ALL_COLUMNS.find(c => c.id === id))
    .filter(Boolean);

  const renderResizableHeader = (column, label, sortable = true, filterable = false, children = null) => (
    <th 
      key={column}
      draggable={column !== 'actions'}
      onDragStart={(e) => handleDragStart(e, column)}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDrop={(e) => handleDrop(e, column)}
      className="text-left px-3 py-3 font-semibold text-gray-700 relative group cursor-grab active:cursor-grabbing"
      style={{ width: columnWidths[column], minWidth: MIN_COLUMN_WIDTHS[column] || 50 }}
    >
      <div className="flex items-center gap-1">
        {children ? children : sortable ? (
          <button 
            onClick={() => handleSort(column)} 
            className="flex items-center hover:text-gray-900"
          >
            {label} <SortIcon column={column} sortColumn={sortColumn} sortDirection={sortDirection} />
          </button>
        ) : (
          <span>{label}</span>
        )}
        {filterable && (columnUniqueValues[column]?.length > 0) && (
          <div className="relative" ref={openFilterColumn === column ? filterDropdownRef : null}>
            <button
              onClick={(e) => { e.stopPropagation(); setOpenFilterColumn(openFilterColumn === column ? null : column); }}
              className={`ml-1 p-0.5 rounded hover:bg-gray-200 transition-colors ${columnFilters[column] ? 'text-blue-600 bg-blue-50' : 'text-gray-400'}`}
              title={columnFilters[column] ? `Filtered: ${columnFilters[column]}` : `Filter by ${label}`}
              data-testid={`filter-icon-${column}`}
            >
              <Funnel size={12} weight={columnFilters[column] ? 'fill' : 'regular'} />
            </button>
            {openFilterColumn === column && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[180px] max-h-[280px] overflow-hidden" data-testid={`filter-dropdown-${column}`}>
                <div className="p-2 border-b border-gray-100">
                  <div className="text-xs font-medium text-gray-500 mb-1">Filter by {label}</div>
                  <button
                    onClick={() => { setColumnFilters(prev => { const next = {...prev}; delete next[column]; return next; }); setOpenFilterColumn(null); }}
                    className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-50 ${!columnFilters[column] ? 'font-medium text-blue-600' : 'text-gray-600'}`}
                    data-testid={`filter-option-${column}-all`}
                  >
                    All
                  </button>
                </div>
                <div className="overflow-y-auto max-h-[200px] p-1">
                  {(columnUniqueValues[column] || []).map(val => (
                    <button
                      key={val}
                      onClick={() => { setColumnFilters(prev => ({...prev, [column]: val})); setOpenFilterColumn(null); }}
                      className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-50 truncate ${columnFilters[column] === val ? 'font-medium text-blue-600 bg-blue-50' : 'text-gray-700'}`}
                      title={val}
                      data-testid={`filter-option-${column}-${val}`}
                    >
                      {column === 'review_status' ? ({'open':'Open','in_progress':'In Progress','in_review':'In Review','approved':'Approved','closed':'Closed','rejected':'Rejected','draft':'Draft','available':'Available','pending_pm':'Pending PM'}[val] || val) : val}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div
        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300"
        onMouseDown={(e) => startResize(column, e)}
      />
    </th>
  );

  // Filtered and sorted documents
  const sortedDocuments = useMemo(() => {
    // First, apply all filters
    let filtered = documents.filter(doc => {
      // Search filter (server-side already filters, but keep for consistency)
      const matchesSearch = !searchQuery || 
        doc.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.document_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Date filter
        (doc.revision_date && doc.revision_date.startsWith(searchDate)) ||
        (doc.created_at && doc.created_at.startsWith(searchDate)) ||
        (doc.last_revision_date && doc.last_revision_date.startsWith(searchDate));
      
      // Regular filters
      const matchesDiscipline = !filters.discipline || doc.discipline === filters.discipline;
      const matchesDocType = !filters.document_type || doc.document_type === filters.document_type;
      const matchesProjectCode = !filters.project_code || doc.project_code === filters.project_code;
      const matchesPriority = !filters.priority || doc.priority === filters.priority;
      const matchesApproval = !filters.approval_status || doc.approval_status === filters.approval_status;

      // Per-column header filters
      const matchesColumnFilters = Object.entries(columnFilters).every(([colId, filterVal]) => {
        if (!filterVal) return true;
        return getColumnValue(doc, colId) === filterVal;
      });
      
      // Date filter - match documents by the displayed Date column
      let matchesDate = true;
      if (searchDate) {
        const selectedDate = new Date(searchDate + 'T00:00:00');
        const docDateStr = doc.last_revision_date || doc.created_at || doc.updated_at;
        if (docDateStr) {
          const docDate = new Date(String(docDateStr).endsWith('Z') || String(docDateStr).includes('+') ? docDateStr : docDateStr + 'Z');
          matchesDate = docDate.getFullYear() === selectedDate.getFullYear() &&
                        docDate.getMonth() === selectedDate.getMonth() &&
                        docDate.getDate() === selectedDate.getDate();
        } else {
          matchesDate = false;
        }
      }
      
      // Tree filters - these take precedence over regular filters
      const matchesTreeDiscipline = !treeFilters.discipline || doc.discipline === treeFilters.discipline;
      const matchesTreeOriginator = !treeFilters.originator || companyKey(doc.source_company || doc.originator) === companyKey(treeFilters.originator);
      const matchesTreeProjectCode = !treeFilters.projectCode || doc.project_code === treeFilters.projectCode;

      // Show-only-selected toggle
      const matchesSelectedOnly = !showOnlySelected || selectedDocuments.size === 0 || selectedDocuments.has(doc.id);
      
      return matchesSearch && matchesDate && matchesSelectedOnly &&
             matchesDiscipline && matchesDocType && matchesProjectCode && matchesPriority && matchesApproval && matchesColumnFilters &&
             matchesTreeDiscipline && matchesTreeOriginator && matchesTreeProjectCode;
    });
    
    // Then sort
    return filtered.sort((a, b) => {
      let sortKey = sortColumn;
      if (sortColumn === 'package') sortKey = 'project_code';
      // review_status column uses latest_status or status field on the document
      if (sortColumn === 'review_status') {
        const aStatus = a.latest_status || a.status || 'draft';
        const bStatus = b.latest_status || b.status || 'draft';
        const cmp = aStatus.toLowerCase().localeCompare(bStatus.toLowerCase());
        return sortDirection === 'asc' ? cmp : -cmp;
      }
      let aVal = a[sortKey];
      let bVal = b[sortKey];

      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      if (sortColumn === 'revision') {
        const cmp = compareRevisions(aVal, bVal);
        return sortDirection === 'asc' ? cmp : -cmp;
      }

      if (sortColumn === 'created_at' || sortColumn === 'updated_at' || sortColumn === 'revision_date' || sortColumn === 'last_revision_date' || sortColumn === 'last_comment_date') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [documents, sortColumn, sortDirection, searchQuery, searchDate, filters, treeFilters, columnFilters, getColumnValue, showOnlySelected, selectedDocuments]);

  // Pagination
  const totalPages = Math.ceil(sortedDocuments.length / pageSize);
  const paginatedDocuments = sortedDocuments.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  useEffect(() => { setCurrentPage(1); }, [searchQuery, searchDate, filters, treeFilters, columnFilters, pageSize, showOnlySelected]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (!uploadData.name) {
        setUploadData({ ...uploadData, name: file.name });
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile && !uploadData.name) {
      toast.error('Please provide a title or select a file');
      return;
    }

    const formData = new FormData();
    if (selectedFile) {
      formData.append('file', selectedFile);
    }
    formData.append('name', uploadData.name || (selectedFile ? selectedFile.name : 'Untitled Document'));
    formData.append('title', uploadData.name || (selectedFile ? selectedFile.name : 'Untitled Document'));
    if (currentFolder) formData.append('folder_id', currentFolder);
    if (uploadData.tags) formData.append('tags', uploadData.tags);
    
    // Auto-assign to selected project if filtering by project
    if (selectedProject) {
      formData.append('project_id', selectedProject);
    }
    
    // Append all fields if they have values
    const fields = [
      'document_number', 'revision', 'revision_date', 'company_document_nr',
      'discipline', 'discipline_code', 'document_type', 'subclass_code', 'classification',
      'project_code', 'project_title', 'work_package_code', 'purchase_order', 'asset_item',
      'originator', 'source_company', 'responsible_engineer', 'company_code',
      'approval_status', 'priority', 'current_review_status',
      'planned_start', 'forecast_start', 'actual_start', 'planned_end', 'forecast_end',
      'external_refs', 'language', 'system_main_division'
    ];
    
    fields.forEach(field => {
      if (uploadData[field]) formData.append(field, uploadData[field]);
    });

    try {
      await axios.post(`${API}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully');
      setUploadOpen(false);
      setSelectedFile(null);
      setUploadData({ 
        name: '', description: '', tags: '',
        document_number: '', revision: '', revision_date: '', company_document_nr: '',
        discipline: '', discipline_code: '', document_type: '', subclass_code: '', classification: '',
        project_code: '', project_title: '', work_package_code: '', purchase_order: '', asset_item: '',
        originator: '', responsible_engineer: '', company_code: '',
        approval_status: '', priority: '', current_review_status: '',
        planned_start: '', forecast_start: '', actual_start: '', planned_end: '', forecast_end: '',
        external_refs: '', language: '', system_main_division: ''
      });
      fetchDocuments();
      fetchMetadataSuggestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/folders?name=${encodeURIComponent(newFolderName)}&parent_id=${currentFolder || ''}`);
      toast.success('Folder created successfully');
      setFolderOpen(false);
      setNewFolderName('');
      fetchFolders();
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  const handleDeleteFolder = async (folderId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/folders/${folderId}`);
      toast.success('Folder deleted');
      fetchFolders();
      if (currentFolder === folderId) setCurrentFolder(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete folder');
    }
  };

  const handleDownload = async (doc) => {
    try {
      // Preferred path: direct storage URL, so large files stream from GCS
      // without passing through (and timing out) the backend.
      try {
        const urlRes = await axios.get(`${API}/documents/${doc.id}/download-url`);
        if (urlRes.data?.url) {
          const link = document.createElement('a');
          link.href = urlRes.data.url;
          link.setAttribute('download', urlRes.data.filename || doc.name || 'document');
          document.body.appendChild(link);
          link.click();
          link.remove();
          toast.success('Download started');
          return;
        }
      } catch (urlError) {
        console.log('Direct download URL unavailable, falling back to proxy:', urlError);
      }

      // First, try to get the latest revision's PDF
      const cyclesResponse = await axios.get(`${API}/cycles?document_id=${doc.id}`);
      const cycles = cyclesResponse.data;
      
      if (cycles && cycles.length > 0) {
        // Sort by created_at descending to get the latest
        const latestCycle = getLatestRevision(cycles, 'revision');
        
        if (latestCycle.pdf_file_name) {
          // Download the latest revision's PDF
          try {
            const response = await axios.get(`${API}/cycles/${latestCycle.id}/download-pdf`, {
              responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', latestCycle.pdf_file_name);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Downloaded latest revision PDF: ${latestCycle.revision}`);
            return;
          } catch (cycleError) {
            console.log('Cycle PDF download failed, trying original document:', cycleError);
            // Continue to fallback
          }
        }
      }
      
      // Fallback: Download original document if no revision PDF exists
      const response = await axios.get(`${API}/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      
      // Check if response is an error (JSON instead of blob)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Download failed');
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.name || 'document.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded');
    } catch (error) {
      console.error('Download error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Download failed';
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (docId, docName) => {
    if (!window.confirm(`Are you sure you want to delete "${docName}"?`)) return;
    
    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  // Transfer document to another project (System Admin only)
  const handleOpenTransferDialog = (doc) => {
    setDocumentToTransfer(doc);
    setTargetProjectId('');
    setTransferDialogOpen(true);
  };

  const handleTransferDocument = async () => {
    if (!documentToTransfer || !targetProjectId) {
      toast.error('Please select a target project');
      return;
    }
    
    try {
      await axios.post(`${API}/documents/${documentToTransfer.id}/transfer`, {
        project_id: targetProjectId
      });
      toast.success('Document transferred successfully');
      setTransferDialogOpen(false);
      setDocumentToTransfer(null);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer document');
    }
  };

  const handleEditDoc = (doc) => {
    setEditingDoc(doc);
    setEditDocData({
      name: doc.name || '',
      description: doc.description || '',
      document_number: doc.document_number || '',
      title: doc.title || '',
      revision: doc.revision || '',
      revision_date: doc.revision_date ? doc.revision_date.split('T')[0] : '',
      discipline: doc.discipline || '',
      discipline_code: doc.discipline_code || '',
      document_type: doc.document_type || '',
      sub_type: doc.sub_type || '',
      classification: doc.classification || '',
      project_code: doc.project_code || '',
      project_title: doc.project_title || '',
      company_code: doc.company_code || '',
      contract_number: doc.contract_number || '',
      purchase_order: doc.purchase_order || '',
      source_company: doc.source_company || '',
      priority: doc.priority || '',
      confidentiality: doc.confidentiality || '',
      notes: doc.notes || '',
      tags: Array.isArray(doc.tags) ? doc.tags.join(', ') : ''
    });
    setEditDocOpen(true);
  };

  const handleSaveDocEdit = async () => {
    if (!editingDoc) return;
    
    try {
      const updateData = { ...editDocData };
      // Convert tags string to array
      if (updateData.tags) {
        updateData.tags = updateData.tags.split(',').map(t => t.trim()).filter(t => t);
      } else {
        updateData.tags = [];
      }
      
      await axios.put(`${API}/documents/${editingDoc.id}`, updateData);
      toast.success('Document updated successfully');
      setEditDocOpen(false);
      setEditingDoc(null);
      fetchDocuments();
    } catch (error) {
      console.error('Failed to update document:', error);
      toast.error(error.response?.data?.detail || 'Failed to update document');
    }
  };

  const toggleFolder = (folderId) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const handleSelectDocument = (docId) => {
    const newSelected = new Set(selectedDocuments);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedDocuments(newSelected);
    setSelectAll(newSelected.size === sortedDocuments.length && sortedDocuments.length > 0);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedDocuments(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(sortedDocuments.map(doc => doc.id));
      setSelectedDocuments(allIds);
      setSelectAll(true);
    }
  };

  const handleCreateTransmittal = async () => {
    if (!transmittalData.transmittal_nr) {
      toast.error('Please enter a transmittal number');
      return;
    }
    try {
      const response = await axios.post(`${API}/transmittals/from-documents`, {
        transmittal_nr: transmittalData.transmittal_nr,
        attn: transmittalData.attn,
        purpose: transmittalData.purpose,
        document_ids: Array.from(selectedDocuments),
        return_codes: transmittalData.return_codes,
        remarks: transmittalData.remarks
      });
      const transId = response.data.id;
      const skippedDocs = response.data?.skipped_document_ids?.length || 0;
      toast.success(`Transmittal created with ${response.data?.document_count ?? selectedDocuments.size} document(s)`);
      if (skippedDocs > 0) toast.error(`${skippedDocs} selected document(s) could not be included (no longer exist)`);
      setTransmittalOpen(false);
      setTransmittalData({ transmittal_nr: '', attn: '', purpose: [], return_codes: {}, remarks: {} });
      const downloadPdf = window.confirm('Download transmittal as PDF? (Cancel for Excel)');
      if (downloadPdf) {
        const pdfRes = await axios.get(`${API}/transmittals/${transId}/download-pdf`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([pdfRes.data]));
        const a = document.createElement('a'); a.href = url;
        a.download = `${transmittalData.transmittal_nr}.pdf`;
        a.click(); window.URL.revokeObjectURL(url);
      } else {
        const xlRes = await axios.get(`${API}/transmittals/${transId}/download-excel`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([xlRes.data]));
        const a = document.createElement('a'); a.href = url;
        a.download = `${transmittalData.transmittal_nr}.xlsx`;
        a.click(); window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transmittal');
    }
  };

  const runMultiSearch = async () => {
    const raw = (multiSearchInput || '').trim();
    if (!raw) { toast.error('Paste at least one document number'); return; }
    setMultiSearchLoading(true);
    try {
      const numbers = raw.split(/[\s,;\n\r\t]+/).filter(Boolean);
      const res = await axios.post(`${API}/documents/multi-search`, { numbers });
      setMultiSearchResult(res.data);
      const matchedIds = new Set((res.data.documents || []).map(d => d.id));
      setDocuments(prev => prev.filter(d => matchedIds.has(d.id)).concat(
        (res.data.documents || []).filter(d => !prev.some(p => p.id === d.id))
      ));
      toast.success(`Matched ${res.data.matched} of ${res.data.requested} document number(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Multi-search failed');
    } finally { setMultiSearchLoading(false); }
  };

  const handleOpenBulkTransfer = () => {
    if (selectedDocuments.size === 0) { toast.error('Select documents first'); return; }
    setBulkTransferProjectId('');
    setBulkTransferOpen(true);
  };

  const runBulkTransfer = async () => {
    if (!bulkTransferProjectId) { toast.error('Choose a target project'); return; }
    setBulkTransferSubmitting(true);
    try {
      const ids = Array.from(selectedDocuments);
      const res = await axios.post(`${API}/documents/bulk-transfer`, {
        document_ids: ids, project_id: bulkTransferProjectId,
      });
      toast.success(res.data.message || 'Documents transferred');
      setBulkTransferOpen(false);
      setSelectedDocuments(new Set());
      fetchDocuments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk transfer failed');
    } finally { setBulkTransferSubmitting(false); }
  };

  const handleBulkDownload = async () => {
    try {
      let downloadCount = 0;
      for (const docId of selectedDocuments) {
        const doc = documents.find(d => d.id === docId);
        if (doc) {
          // First, try to get the latest revision's PDF
          try {
            const cyclesResponse = await axios.get(`${API}/cycles?document_id=${docId}`);
            const cycles = cyclesResponse.data;
            
            if (cycles && cycles.length > 0) {
              // Sort by created_at descending to get the latest
              cycles.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
              const latestCycle = cycles[0];
              
              if (latestCycle.pdf_file_name) {
                // Download the latest revision's PDF
                const response = await axios.get(`${API}/cycles/${latestCycle.id}/download-pdf`, {
                  responseType: 'blob'
                });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', latestCycle.pdf_file_name);
                document.body.appendChild(link);
                link.click();
                link.remove();
                downloadCount++;
                await new Promise(resolve => setTimeout(resolve, 500)); // Delay between downloads
                continue;
              }
            }
          } catch (cycleError) {
            console.log('No revisions found for document:', docId);
          }
          
          // Fallback: Download original document
          const response = await axios.get(`${API}/documents/${docId}/download`, {
            responseType: 'blob'
          });
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', doc.name);
          document.body.appendChild(link);
          link.click();
          link.remove();
          downloadCount++;
          await new Promise(resolve => setTimeout(resolve, 500)); // Delay between downloads
        }
      }
      toast.success(`Downloaded ${downloadCount} documents (latest revision PDFs when available)`);
    } catch (error) {
      toast.error('Failed to download some documents');
    }
  };

  // v18: Bulk ZIP download (PDFs only, or PDFs + Natives)
  const handleBulkDownloadZip = async (includeNative) => {
    const ids = Array.from(selectedDocuments);
    if (ids.length === 0) {
      toast.error('Select at least one document first');
      return;
    }
    const toastId = toast.loading(
      includeNative
        ? `Preparing ZIP with PDFs + Natives for ${ids.length} document(s)...`
        : `Preparing ZIP with PDFs for ${ids.length} document(s)...`
    );
    try {
      // Streamed straight from storage: no size ceiling, no request timeout
      const prep = await axios.post(`${API}/documents/bulk-download/prepare`, {
        document_ids: ids,
        include_native: !!includeNative,
      });
      const token = prep.data?.token;
      if (!token) throw new Error('Could not prepare download');
      const link = document.createElement('a');
      link.href = `${API}/documents/bulk-download/${token}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(
        `ZIP download started for ${prep.data.file_count} document(s) — large batches keep downloading in the background`,
        { id: toastId }
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Bulk ZIP download failed';
      toast.error(`ZIP download failed: ${msg}`, { id: toastId });
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedDocuments.size} documents?`)) return;
    
    try {
      const deletePromises = [];
      for (const docId of selectedDocuments) {
        deletePromises.push(axios.delete(`${API}/documents/${docId}`));
      }
      await Promise.all(deletePromises);
      toast.success(`Deleted ${selectedDocuments.size} documents`);
      setSelectedDocuments(new Set());
      setSelectAll(false);
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete some documents');
      fetchDocuments(); // Refresh to show which ones were deleted
    }
  };

  // Open multi-document popup editor
  const handleBulkEdit = () => {
    const docsToEdit = documents.filter(doc => selectedDocuments.has(doc.id));
    if (docsToEdit.length === 0) {
      toast.error('No documents selected');
      return;
    }
    setDocumentsToEdit(docsToEdit);
    setMultiEditOpen(true);
  };

  const handleCloseMultiEdit = () => {
    setMultiEditOpen(false);
    setDocumentsToEdit([]);
  };

  const handleMultiEditComplete = () => {
    fetchDocuments();
    setSelectedDocuments(new Set());
    setSelectAll(false);
  };

  const buildFolderTree = (folders, parentId = null) => {
    return folders
      .filter(f => {
        const folderParentId = f.parent_id || null;
        return folderParentId === parentId;
      })
      .map(folder => ({
        ...folder,
        children: buildFolderTree(folders, folder.id)
      }));
  };

  const renderFolderTree = (folderNodes, level = 0) => {
    return folderNodes.map(folder => {
      const isExpanded = expandedFolders.has(folder.id);
      const isActive = currentFolder === folder.id;
      const hasChildren = folder.children && folder.children.length > 0;

      return (
        <div key={folder.id}>
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <div
                className={`folder-tree-item flex items-center gap-2 pl-${level * 4} ${isActive ? 'active' : ''}`}
                onClick={() => setCurrentFolder(folder.id)}
                data-testid={`folder-${folder.id}`}
              >
                {hasChildren && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFolder(folder.id);
                    }}
                    className="p-0"
                  >
                    {isExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
                  </button>
                )}
                {!hasChildren && <span className="w-4" />}
                {isExpanded ? <FolderOpen size={18} weight="regular" /> : <Folder size={18} weight="regular" />}
                <span className="text-sm">{folder.name}</span>
              </div>
            </ContextMenuTrigger>
            <ContextMenuContent>
              <ContextMenuItem 
                onClick={() => handleDeleteFolder(folder.id, { stopPropagation: () => {} })} 
                className="text-red-600 cursor-pointer"
              >
                <Trash size={14} className="mr-2" />
                Delete Folder
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>
          {isExpanded && hasChildren && (
            <div className="ml-4">
              {renderFolderTree(folder.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  const folderTree = buildFolderTree(folders);

  const formatDateOnly = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(String(dateString).endsWith('Z') || String(dateString).includes('+') ? dateString : dateString + 'Z');
    return d.toLocaleDateString();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Autocomplete input for metadata fields using native datalist
  const AutocompleteInput = ({ field, value, onChange, placeholder, className = "rounded-sm h-8 text-sm" }) => {
    const suggestions = metadataSuggestions[field] || [];
    const listId = `suggestions-${field}`;
    return (
      <>
        <Input
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={className}
          list={listId}
          data-testid={`autocomplete-${field}`}
        />
        <datalist id={listId}>
          {suggestions.map((s, i) => (
            <option key={i} value={s} />
          ))}
        </datalist>
      </>
    );
  };

  const toggleTree = (treeName) => {
    setExpandedTrees(prev => ({ ...prev, [treeName]: !prev[treeName] }));
  };

  const handleTreeFilterClick = (filterType, value) => {
    setTreeFilters(prev => {
      const updated = { ...prev, [filterType]: prev[filterType] === value ? null : value };
      localStorage.setItem('docTreeFilters', JSON.stringify(updated));
      return updated;
    });
  };

  const clearAllTreeFilters = () => {
    const cleared = { discipline: null, originator: null, projectCode: null };
    setTreeFilters(cleared);
    localStorage.removeItem('docTreeFilters');
  };

  const hasActiveTreeFilters = Object.values(treeFilters).some(v => v !== null);

  // Render tree section
  const renderTreeSection = (title, Icon, treeName, items, filterType, selectedValue) => (
    <div className="border-b border-border" key={treeName}>
      <div
        className="flex items-center gap-2 p-3 cursor-pointer hover:bg-gray-50 select-none"
        onClick={() => toggleTree(treeName)}
      >
        {expandedTrees[treeName] ? <CaretDown size={14} /> : <CaretRight size={14} />}
        <Icon size={16} className="text-muted-foreground" />
        <span className="text-sm font-medium flex-1">{title}</span>
        {items.length > 0 && (
          <span className="text-base text-muted-foreground bg-gray-100 px-1.5 rounded">{items.length}</span>
        )}
      </div>
      {expandedTrees[treeName] && (
        <div className="pb-2 pl-6">
          {items.length === 0 ? (
            <p className="text-base text-muted-foreground py-1 px-2">No items</p>
          ) : (
            items.map((item, idx) => (
              <div
                key={idx}
                className={`flex items-center gap-2 py-1.5 px-2 cursor-pointer rounded-sm text-sm hover:bg-gray-100 ${
                  selectedValue === item ? 'bg-primary/10 text-primary font-medium' : ''
                }`}
                onClick={() => handleTreeFilterClick(filterType, item)}
              >
                <FileText size={14} className="text-muted-foreground" />
                <span className="truncate">{item}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex h-full" data-testid="documents-page">
      {/* Left sidebar with tree filters (resizable) */}
      <div
        className="relative border-r border-border bg-white overflow-y-auto flex flex-col flex-shrink-0"
        style={{ width: `${filterPanelWidth}px` }}
        data-testid="filter-panel"
      >
        {/* Folders Section */}
        <div className="border-b border-border">
          <div className="p-3 bg-gray-50">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Folders</h3>
              {canEdit && (
              <Dialog open={folderOpen} onOpenChange={setFolderOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" data-testid="new-folder-button">
                    <Plus size={14} />
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="new-folder-dialog">
                  <DialogHeader>
                    <DialogTitle>Create New Folder</DialogTitle>
                    <DialogDescription>Create a folder to organize your documents</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateFolder} className="space-y-4">
                    <div>
                      <Label>Folder Name</Label>
                      <Input
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        required
                        data-testid="folder-name-input"
                        className="rounded-sm"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-primary hover:bg-primary/90 rounded-sm" data-testid="create-folder-submit">
                      Create Folder
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              )}
            </div>
          </div>
          <div className="p-2">
            <div
              className={`folder-tree-item ${!currentFolder ? 'active' : ''}`}
              onClick={() => setCurrentFolder(null)}
              data-testid="root-folder"
            >
              <FolderOpen size={18} weight="regular" />
              <span className="text-sm font-medium">All Documents</span>
            </div>
            {renderFolderTree(folderTree)}
          </div>
        </div>

        {/* Tree Filters Section */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-3 bg-gray-50 border-b border-border flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Filter Trees</h3>
            {hasActiveTreeFilters && (
              <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={clearAllTreeFilters}>
                Clear
              </Button>
            )}
          </div>
          
          {/* Company Tree (Originator / source company based) */}
          {renderTreeSection(
            "Company Tree",
            Buildings,
            "company",
            uniqueValues.originators,
            "originator",
            treeFilters.originator
          )}
          
          {/* Package Tree (Project Code based) */}
          {renderTreeSection(
            "Package Tree",
            Package,
            "package",
            uniqueValues.projectCodes,
            "projectCode",
            treeFilters.projectCode
          )}
          
          {/* Documents Tree (Discipline based) */}
          {renderTreeSection(
            "Documents Tree",
            TreeStructure,
            "documents",
            uniqueValues.disciplines,
            "discipline",
            treeFilters.discipline
          )}
        </div>
        {/* Resize handle */}
        <div
          onMouseDown={startFilterPanelResize}
          className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize bg-transparent hover:bg-blue-400/60 active:bg-blue-500/80 transition-colors z-20"
          title="Drag to resize"
          data-testid="filter-panel-resize-handle"
        />
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-y-auto bg-gray-50 min-w-0">
        {/* Sticky upper ribbon: title + actions + search. Stays visible while scrolling. */}
        <div className="sticky top-0 z-30 bg-gray-50/95 backdrop-blur-sm border-b border-gray-200/60 shadow-sm px-6 pt-6 pb-4" data-testid="documents-ribbon">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">
              Documents
            </h1>
            <div className="flex gap-2 flex-wrap">
              {/* v23: export dropdown menu (latest vs all-revisions) */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="rounded-xl border-gray-200 text-gray-700 hover:bg-gray-100"
                    data-testid="export-excel-button"
                  >
                    <Download size={18} className="mr-2" />
                    {selectedDocuments.size > 0 ? `Export Selected (${selectedDocuments.size})` : 'Export to Excel'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={async () => {
                    try {
                      const params = selectedDocuments.size > 0
                        ? `?doc_ids=${Array.from(selectedDocuments).join(',')}`
                        : '';
                      const response = await axios.get(`${API}/documents/export/excel${params}`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', selectedDocuments.size > 0 ? `documents_export_${selectedDocuments.size}_selected.xlsx` : 'documents_export.xlsx');
                      document.body.appendChild(link); link.click(); link.remove();
                      toast.success(selectedDocuments.size > 0 ? `Exported ${selectedDocuments.size} selected document(s)` : 'All documents exported');
                    } catch (error) { toast.error('Export failed'); }
                  }} data-testid="export-excel-latest">
                    Latest revision only
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={async () => {
                    try {
                      const params = selectedDocuments.size > 0
                        ? `?doc_ids=${Array.from(selectedDocuments).join(',')}`
                        : '';
                      const response = await axios.get(`${API}/documents/export/excel/all-revisions${params}`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', selectedDocuments.size > 0 ? `documents_export_all_revisions_${selectedDocuments.size}_selected.xlsx` : 'documents_export_all_revisions.xlsx');
                      document.body.appendChild(link); link.click(); link.remove();
                      toast.success('All revisions exported');
                    } catch (error) { toast.error('Export failed'); }
                  }} data-testid="export-excel-all-revisions">
                    All revisions
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {canEdit && (
              <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-900 hover:bg-blue-800 text-white rounded-xl" data-testid="upload-button">
                    <UploadSimple size={18} weight="bold" className="mr-2" />
                    Upload Document
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="upload-dialog">
                  <DialogHeader>
                    <DialogTitle className="text-xl">Create Document</DialogTitle>
                    <DialogDescription className="text-gray-500">Create a new document with metadata. File attachment is optional.</DialogDescription>
                  </DialogHeader>
                <form onSubmit={handleUpload} className="space-y-4">
                  {/* File Selection */}
                  <div className="border-b border-gray-100 pb-4">
                    <Label className="font-medium text-gray-700">File (optional)</Label>
                    <Input
                      type="file"
                      onChange={handleFileSelect}
                      data-testid="file-input"
                      className="rounded-sm mt-1"
                    />
                    {selectedFile && (
                      <p className="text-base text-muted-foreground mt-1">
                        Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                      </p>
                    )}
                  </div>
                  
                  {/* Document Identification */}
                  <div className="border-b pb-4">
                    <h4 className="font-medium text-sm mb-3 text-primary">Document Identification</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Title</Label>
                        <Input
                          value={uploadData.name}
                          onChange={(e) => setUploadData({ ...uploadData, name: e.target.value })}
                          data-testid="doc-name-input"
                          className="rounded-sm h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Document Nr.</Label>
                        <Input
                          value={uploadData.document_number}
                          onChange={(e) => setUploadData({ ...uploadData, document_number: e.target.value })}
                          placeholder="DOC-2025-001"
                          className="rounded-sm h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Revision</Label>
                        <Input
                          value={uploadData.revision}
                          onChange={(e) => setUploadData({ ...uploadData, revision: e.target.value })}
                          placeholder="A, B, 01"
                          className="rounded-sm h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Revision Date</Label>
                        <Input
                          type="date"
                          value={uploadData.revision_date}
                          onChange={(e) => setUploadData({ ...uploadData, revision_date: e.target.value })}
                          className="rounded-sm h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Company Document Nr.</Label>
                        <Input
                          value={uploadData.company_document_nr}
                          onChange={(e) => setUploadData({ ...uploadData, company_document_nr: e.target.value })}
                          className="rounded-sm h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">External Refs.</Label>
                        <Input
                          value={uploadData.external_refs}
                          onChange={(e) => setUploadData({ ...uploadData, external_refs: e.target.value })}
                          className="rounded-sm h-8 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Classification */}
                  <div className="border-b pb-4">
                    <h4 className="font-medium text-sm mb-3 text-primary">Classification</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Discipline</Label>
                        <AutocompleteInput
                          field="discipline"
                          value={uploadData.discipline}
                          onChange={(e) => setUploadData({ ...uploadData, discipline: e.target.value })}
                          placeholder="e.g., Mechanical"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Discipline Code</Label>
                        <AutocompleteInput
                          field="discipline_code"
                          value={uploadData.discipline_code}
                          onChange={(e) => setUploadData({ ...uploadData, discipline_code: e.target.value })}
                          placeholder="e.g., ME, EL, CV"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Document Type</Label>
                        <AutocompleteInput
                          field="document_type"
                          value={uploadData.document_type}
                          onChange={(e) => setUploadData({ ...uploadData, document_type: e.target.value })}
                          placeholder="e.g., Drawing"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Subclass Code</Label>
                        <AutocompleteInput
                          field="subclass_code"
                          value={uploadData.subclass_code}
                          onChange={(e) => setUploadData({ ...uploadData, subclass_code: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Classification</Label>
                        <Input
                          value={uploadData.classification}
                          onChange={(e) => setUploadData({ ...uploadData, classification: e.target.value })}
                          placeholder="e.g., IFR, IFA, IFC"
                          className="rounded-sm h-8 text-sm"
                          list="classification-options"
                        />
                        <datalist id="classification-options">
                          {CLASSIFICATION_OPTIONS.map(opt => (
                            <option key={opt} value={opt} />
                          ))}
                        </datalist>
                      </div>
                      <div>
                        <Label className="text-xs">Language</Label>
                        <AutocompleteInput
                          field="language"
                          value={uploadData.language}
                          onChange={(e) => setUploadData({ ...uploadData, language: e.target.value })}
                          placeholder="e.g., English"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Project Information */}
                  <div className="border-b pb-4">
                    <h4 className="font-medium text-sm mb-3 text-primary">Project Information</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Project Code</Label>
                        <AutocompleteInput
                          field="project_code"
                          value={uploadData.project_code}
                          onChange={(e) => setUploadData({ ...uploadData, project_code: e.target.value })}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Project Title</Label>
                        <AutocompleteInput
                          field="project_title"
                          value={uploadData.project_title}
                          onChange={(e) => setUploadData({ ...uploadData, project_title: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Work Package Code</Label>
                        <AutocompleteInput
                          field="work_package_code"
                          value={uploadData.work_package_code}
                          onChange={(e) => setUploadData({ ...uploadData, work_package_code: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Purchase Order</Label>
                        <AutocompleteInput
                          field="purchase_order"
                          value={uploadData.purchase_order}
                          onChange={(e) => setUploadData({ ...uploadData, purchase_order: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Asset Item</Label>
                        <AutocompleteInput
                          field="asset_item"
                          value={uploadData.asset_item}
                          onChange={(e) => setUploadData({ ...uploadData, asset_item: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* People/Company */}
                  <div className="border-b pb-4">
                    <h4 className="font-medium text-sm mb-3 text-primary">People & Company</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Originator</Label>
                        <AutocompleteInput
                          field="originator"
                          value={uploadData.originator}
                          onChange={(e) => setUploadData({ ...uploadData, originator: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Responsible Engineer</Label>
                        <AutocompleteInput
                          field="responsible_engineer"
                          value={uploadData.responsible_engineer}
                          onChange={(e) => setUploadData({ ...uploadData, responsible_engineer: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Company Code</Label>
                        <AutocompleteInput
                          field="company_code"
                          value={uploadData.company_code}
                          onChange={(e) => setUploadData({ ...uploadData, company_code: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Status & Priority */}
                  <div className="border-b pb-4">
                    <h4 className="font-medium text-sm mb-3 text-primary">Status & Priority</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Approval Status</Label>
                        <Input
                          value={uploadData.approval_status}
                          onChange={(e) => setUploadData({ ...uploadData, approval_status: e.target.value })}
                          placeholder="e.g., Draft, Approved"
                          className="rounded-sm h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Priority</Label>
                        <Input
                          value={uploadData.priority}
                          onChange={(e) => setUploadData({ ...uploadData, priority: e.target.value })}
                          placeholder="e.g., High, Medium, Low"
                          className="rounded-sm h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">System/Division</Label>
                        <Input
                          value={uploadData.system_main_division}
                          onChange={(e) => setUploadData({ ...uploadData, system_main_division: e.target.value })}
                          className="rounded-sm h-8 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Date Fields */}
                  <div className="border-b pb-4">
                    <h4 className="font-medium text-sm mb-3 text-primary">Schedule</h4>
                    <div className="grid grid-cols-5 gap-3">
                      <div>
                        <Label className="text-xs">Planned Start</Label>
                        <Input
                          type="date"
                          value={uploadData.planned_start}
                          onChange={(e) => setUploadData({ ...uploadData, planned_start: e.target.value })}
                          className="rounded-sm h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Forecast Start</Label>
                        <Input
                          type="date"
                          value={uploadData.forecast_start}
                          onChange={(e) => setUploadData({ ...uploadData, forecast_start: e.target.value })}
                          className="rounded-sm h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Actual Start</Label>
                        <Input
                          type="date"
                          value={uploadData.actual_start}
                          onChange={(e) => setUploadData({ ...uploadData, actual_start: e.target.value })}
                          className="rounded-sm h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Planned End</Label>
                        <Input
                          type="date"
                          value={uploadData.planned_end}
                          onChange={(e) => setUploadData({ ...uploadData, planned_end: e.target.value })}
                          className="rounded-sm h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Forecast End</Label>
                        <Input
                          type="date"
                          value={uploadData.forecast_end}
                          onChange={(e) => setUploadData({ ...uploadData, forecast_end: e.target.value })}
                          className="rounded-sm h-8 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Description & Tags */}
                  <div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Description</Label>
                        <Input
                          value={uploadData.description}
                          onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                          data-testid="doc-description-input"
                          className="rounded-sm h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Tags (comma-separated)</Label>
                        <Input
                          value={uploadData.tags}
                          onChange={(e) => setUploadData({ ...uploadData, tags: e.target.value })}
                          placeholder="tag1, tag2, tag3"
                          data-testid="doc-tags-input"
                          className="rounded-sm h-8 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <Button type="submit" className="w-full bg-primary hover:bg-primary/90 rounded-sm" data-testid="upload-submit">
                    Upload Document
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            )}
            </div>
          </div>

          {/* Search */}
          <div>
            <div className="flex gap-2 items-center flex-wrap">
              <div className="flex-1 relative">
                <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search documents by name, number, project..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); localStorage.setItem('docSearchQuery', e.target.value); }}
                  onKeyDown={(e) => e.key === 'Enter' && fetchDocuments()}
                  className="pl-10 rounded-sm"
                  data-testid="search-input"
                />
              </div>
              <Input
                type="date"
                value={searchDate}
                onChange={(e) => setSearchDate(e.target.value)}
                className="w-40 rounded-sm"
                data-testid="search-date"
              />
              {searchDate && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setSearchDate('')}
                  className="rounded-sm"
                >
                  Clear Date
                </Button>
              )}
              <Button 
                size="sm" 
                onClick={fetchDocuments}
                className="rounded-sm bg-primary"
              >
                Search
              </Button>
              {(user?.is_super_admin || (user?.role || '').toLowerCase() === 'admin' || user?.permissions?.can_multi_search) && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setMultiSearchOpen(true)}
                  className="rounded-sm border-blue-200 text-blue-700"
                  data-testid="multi-search-btn"
                >
                  Multi-Search
                </Button>
              )}
            </div>
            
            {/* Active Filters Display & Results Count */}
            {(hasActiveTreeFilters || searchQuery || selectedProject || sortedDocuments.length !== documents.length) && (
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="flex flex-wrap gap-2">
                  {selectedProject && (
                    <span className="inline-flex items-center gap-1 text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded">
                      Project filter active (change in header)
                    </span>
                  )}
                  {treeFilters.discipline && (
                    <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                      Discipline: {treeFilters.discipline}
                      <button onClick={() => handleTreeFilterClick('discipline', treeFilters.discipline)} className="hover:text-primary/70">×</button>
                    </span>
                  )}
                  {treeFilters.disciplineCode && (
                    <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                      Discipline Code: {treeFilters.disciplineCode}
                      <button onClick={() => handleTreeFilterClick('disciplineCode', treeFilters.disciplineCode)} className="hover:text-primary/70">×</button>
                    </span>
                  )}
                  {treeFilters.projectCode && (
                    <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                      Package: {treeFilters.projectCode}
                      <button onClick={() => handleTreeFilterClick('projectCode', treeFilters.projectCode)} className="hover:text-primary/70">×</button>
                    </span>
                  )}
                  {treeFilters.originator && (
                    <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                      Company: {treeFilters.originator}
                      <button onClick={() => handleTreeFilterClick('originator', treeFilters.originator)} className="hover:text-primary/70">×</button>
                    </span>
                  )}
                </div>
                {sortedDocuments.length !== documents.length && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="font-medium">{sortedDocuments.length}</span>
                    <span>of</span>
                    <span className="font-medium">{documents.length}</span>
                    <span>documents</span>
                    {hasActiveTreeFilters && (
                      <button 
                        onClick={clearAllTreeFilters}
                        className="text-primary hover:underline text-xs ml-2"
                      >
                        Clear all filters
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Scrollable content below sticky ribbon */}
        <div className="px-6 py-6">
          {/* Documents table */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-apple-sm">
              <FileText size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No documents found</p>
            </div>
          ) : (
            <>
              {/* Bulk actions bar */}
              {selectedDocuments.size > 0 && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-xl flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-900">
                    {selectedDocuments.size} document{selectedDocuments.size !== 1 ? 's' : ''} selected
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={showOnlySelected ? 'default' : 'outline'}
                      onClick={() => setShowOnlySelected(v => !v)}
                      className={showOnlySelected ? 'rounded-lg bg-blue-600 hover:bg-blue-700 text-white' : 'rounded-lg border-blue-200 text-blue-700 hover:bg-blue-100'}
                      data-testid="show-only-selected-btn"
                    >
                      <Eye size={14} className="mr-2" />
                      {showOnlySelected ? 'Show All' : 'Show Selected Only'}
                    </Button>
                    {canEdit && (
                    <Button size="sm" variant="outline" onClick={handleBulkEdit} className="rounded-lg border-blue-200 text-blue-700 hover:bg-blue-100" data-testid="bulk-edit-btn">
                      <SquaresFour size={14} className="mr-2" />
                      Edit Selected
                    </Button>
                    )}
                    {(canDownloadPdf || canDownloadNative) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" className="rounded-lg border-blue-200 text-blue-700 hover:bg-blue-100" data-testid="bulk-download-trigger">
                          <Download size={14} className="mr-2" />
                          Download
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem onClick={() => handleBulkDownloadZip(false)} data-testid="bulk-download-pdfs">
                          Download PDFs (ZIP)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleBulkDownloadZip(true)} data-testid="bulk-download-pdfs-natives">
                          Download PDFs + Natives (ZIP)
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setIncomingTransmittalOpen(true)} className="rounded-lg border-green-200 text-green-700 hover:bg-green-50" data-testid="create-incoming-transmittal-btn">
                      <ArrowSquareIn size={14} className="mr-2" />
                      Create Incoming Transmittal
                    </Button>
                    <Button size="sm" variant="outline" onClick={async () => {
                      try {
                        const resp = await axios.post(`${API}/transmittals/document-review-codes`, {
                          document_ids: Array.from(selectedDocuments)
                        });
                        setTransmittalData(prev => ({...prev, return_codes: {...resp.data, ...prev.return_codes}}));
                      } catch(e) { console.log('Could not fetch review codes'); }
                      setTransmittalOpen(true);
                    }} className="rounded-lg border-green-200 text-green-700 hover:bg-green-50" data-testid="create-outgoing-transmittal-btn">
                      <ArrowSquareOut size={14} className="mr-2" />
                      Create Outgoing Transmittal
                    </Button>
                    {isSuperAdmin && (
                    <Button size="sm" variant="outline" onClick={handleOpenBulkTransfer} className="rounded-lg border-purple-200 text-purple-700 hover:bg-purple-50" data-testid="bulk-transfer-btn">
                      <ArrowSquareOut size={14} className="mr-2" />
                      Transfer to Project
                    </Button>
                    )}
                    {canEdit && (
                    <Button size="sm" variant="outline" onClick={handleBulkDelete} className="rounded-lg border-red-200 text-red-600 hover:bg-red-50">
                      <Trash size={14} className="mr-2" />
                      Delete
                    </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => { setSelectedDocuments(new Set()); setSelectAll(false); setShowOnlySelected(false); }} className="text-blue-600">
                      Clear
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="bg-white rounded-2xl border border-gray-100 shadow-apple-sm overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full" data-testid="documents-table" style={{ minWidth: '1100px' }}>
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th 
                        className="text-left px-3 py-3 font-semibold text-gray-700 relative group"
                        style={{ width: columnWidths.checkbox, minWidth: columnWidths.checkbox }}
                      >
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="rounded"
                          data-testid="select-all-checkbox"
                        />
                        <div
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300"
                          onMouseDown={(e) => startResize('checkbox', e)}
                        />
                      </th>
                      {orderedColumns.map(col => renderResizableHeader(col.id, col.label, col.sortable, col.filterable))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedDocuments.map((doc) => (
                    <ContextMenu key={doc.id}>
                      <ContextMenuTrigger asChild>
                        <tr className="document-list-item border-b border-border hover:bg-gray-50 transition-colors cursor-context-menu" data-testid={`doc-row-${doc.id}`}>
                      <td className="px-3 py-2" style={{ width: columnWidths.checkbox }}>
                        <input
                          type="checkbox"
                          checked={selectedDocuments.has(doc.id)}
                          onChange={() => handleSelectDocument(doc.id)}
                          className="rounded-sm"
                          data-testid={`select-doc-${doc.id}`}
                        />
                      </td>
                      {orderedColumns.map(col => {
                        const cw = columnWidths[col.id];
                        switch(col.id) {
                          case 'document_number':
                            return <td key={col.id} className="px-3 py-2" style={{ width: cw }}><span className="text-base text-gray-900 font-mono font-medium">{doc.document_number || '-'}</span></td>;
                          case 'title':
                            return <td key={col.id} className="px-3 py-2" style={{ width: cw, maxWidth: cw }}><div className="flex items-center gap-2"><FileText size={14} weight="regular" className="flex-shrink-0 text-muted-foreground" /><span className="text-base font-medium truncate" title={doc.title || doc.name}>{doc.title || doc.name}</span></div></td>;
                          case 'discipline':
                            return <td key={col.id} className="px-3 py-2"><span className="text-base text-muted-foreground">{doc.discipline || '-'}</span></td>;
                          case 'package':
                            return <td key={col.id} className="px-3 py-2"><span className="text-base text-muted-foreground">{doc.project_code || '-'}</span></td>;
                          case 'revision':
                            return <td key={col.id} className="px-3 py-2 text-center"><span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-gray-100 text-xs font-mono font-medium text-gray-700">{doc.latest_revision || doc.revision || '-'}</span></td>;
                          case 'review_status':
                            return <td key={col.id} className="px-3 py-2">{(() => {
                              const status = doc.latest_status || doc.status || 'draft';
                              const statusColors = { 'open': 'bg-blue-100 text-blue-700', 'in_progress': 'bg-yellow-100 text-yellow-700', 'in_review': 'bg-yellow-100 text-yellow-700', 'approved': 'bg-green-100 text-green-700', 'closed': 'bg-green-100 text-green-700', 'rejected': 'bg-red-100 text-red-700', 'draft': 'bg-gray-100 text-gray-600' };
                              const statusLabels = { 'open': 'Open', 'in_progress': 'In Progress', 'in_review': 'In Review', 'approved': 'Approved', 'closed': 'Closed', 'rejected': 'Rejected', 'draft': 'Draft' };
                              return <span className={`text-xs px-2 py-1 rounded-md font-medium ${statusColors[status] || statusColors['draft']}`}>{statusLabels[status] || status}</span>;
                            })()}</td>;
                          case 'classification':
                            return <td key={col.id} className="px-3 py-2"><span className="text-base text-muted-foreground">{doc.classification || '-'}</span></td>;
                          case 'revision_date':
                            return <td key={col.id} className="px-3 py-2 text-base text-muted-foreground">{doc.revision_date ? formatDateOnly(doc.revision_date) : '-'}</td>;
                          case 'review_code_label':
                            return <td key={col.id} className="px-3 py-2"><span className="text-base text-muted-foreground">{doc.review_code_label || '-'}</span></td>;
                          case 'last_comment_date':
                            return <td key={col.id} className="px-3 py-2 text-base text-muted-foreground">{doc.last_comment_date ? formatDate(doc.last_comment_date) : '-'}</td>;
                                                    case 'outgoing_transmittal_nr':
                            return <td key={col.id} className="px-3 py-2"><span className="text-xs text-muted-foreground">{doc.outgoing_transmittal_nr || '-'}</span></td>;
                          case 'last_revision_date':
                            return <td key={col.id} className="px-3 py-2 text-base text-muted-foreground">{formatDate(doc.last_revision_date || doc.created_at)}</td>;
                          case 'actions':
                            return <td key={col.id} className="px-3 py-2"><div className="flex items-center justify-end gap-1">
                              <Link to={`/documents/${doc.id}/cycles`}><Button size="sm" variant="ghost" title="Document Revisions" data-testid={`cycles-${doc.id}`} className="h-7 w-7 p-0"><GitBranch size={14} /></Button></Link>
                              {canEdit && <Button size="sm" variant="ghost" onClick={() => handleEditDoc(doc)} data-testid={`edit-${doc.id}`} className="h-7 w-7 p-0" title="Edit document"><PencilSimple size={14} className="text-blue-600" /></Button>}
                              {(canDownloadPdf || canDownloadNative) && <Button size="sm" variant="ghost" onClick={() => handleDownload(doc)} data-testid={`download-${doc.id}`} className="h-7 w-7 p-0"><Download size={14} /></Button>}
                              {canEdit && <Button size="sm" variant="ghost" onClick={() => handleDelete(doc.id, doc.name)} data-testid={`delete-${doc.id}`} className="h-7 w-7 p-0"><Trash size={14} className="text-destructive" /></Button>}
                            </div></td>;
                          default:
                            return <td key={col.id} className="px-3 py-2">-</td>;
                        }
                      })}
                    </tr>
                      </ContextMenuTrigger>
                      <ContextMenuContent className="w-56">
                        <ContextMenuItem onClick={() => handlePreview(doc)} className="cursor-pointer">
                          <Eye size={14} className="mr-2" />
                          View / Preview
                        </ContextMenuItem>
                        {canEdit && (
                        <ContextMenuItem onClick={() => handleEditDoc(doc)} className="cursor-pointer">
                          <PencilSimple size={14} className="mr-2" />
                          Edit Document
                        </ContextMenuItem>
                        )}
                        <ContextMenuItem onClick={() => handleViewRevisions(doc)} className="cursor-pointer">
                          <GitBranch size={14} className="mr-2" />
                          View Revisions
                        </ContextMenuItem>
                        <ContextMenuSeparator />
                        {(canDownloadPdf || canDownloadNative) && (
                        <ContextMenuItem onClick={() => handleDownload(doc)} className="cursor-pointer">
                          <Download size={14} className="mr-2" />
                          Download
                        </ContextMenuItem>
                        )}
                        {canEdit && (
                        <ContextMenuItem onClick={() => handleStartReview(doc)} className="cursor-pointer">
                          <ClipboardText size={14} className="mr-2" />
                          Start Review
                        </ContextMenuItem>
                        )}
                        {canEdit && (
                        <>
                        <ContextMenuSeparator />
                        <ContextMenuItem onClick={() => handleContextCreateIncoming(doc)} className="cursor-pointer" data-testid="ctx-create-incoming-transmittal">
                          <ArrowSquareIn size={14} className="mr-2" />
                          Create Incoming Transmittal
                        </ContextMenuItem>
                        <ContextMenuItem onClick={() => handleContextCreateOutgoing(doc)} className="cursor-pointer" data-testid="ctx-create-outgoing-transmittal">
                          <ArrowSquareOut size={14} className="mr-2" />
                          Create Outgoing Transmittal
                        </ContextMenuItem>
                        <ContextMenuSeparator />
                        <ContextMenuSub>
                          <ContextMenuSubTrigger className="cursor-pointer">
                            <FolderSimple size={14} className="mr-2" />
                            Move to Folder
                          </ContextMenuSubTrigger>
                          <ContextMenuSubContent className="w-48">
                            <ContextMenuItem onClick={() => handleMoveToFolder(doc, null)} className="cursor-pointer text-xs">
                              📁 Root (No Folder)
                            </ContextMenuItem>
                            {folders.map(f => (
                              <ContextMenuItem 
                                key={f.id} 
                                onClick={() => handleMoveToFolder(doc, f.id)}
                                className="cursor-pointer text-xs"
                              >
                                📁 {f.name}
                              </ContextMenuItem>
                            ))}
                          </ContextMenuSubContent>
                        </ContextMenuSub>
                        </>
                        )}
                        <ContextMenuItem onClick={() => handleCopyDocNumber(doc)} className="cursor-pointer">
                          <Copy size={14} className="mr-2" />
                          Copy Doc Number
                        </ContextMenuItem>
                        {isSuperAdmin && (
                        <>
                        <ContextMenuSeparator />
                        <ContextMenuItem onClick={() => handleOpenTransferDialog(doc)} className="cursor-pointer text-blue-600">
                          <ArrowSquareOut size={14} className="mr-2" />
                          Transfer to Project
                        </ContextMenuItem>
                        </>
                        )}
                        {canEdit && (
                        <>
                        <ContextMenuSeparator />
                        <ContextMenuItem onClick={() => handleDelete(doc.id, doc.name)} className="cursor-pointer text-red-600">
                          <Trash size={14} className="mr-2" />
                          Delete
                        </ContextMenuItem>
                        </>
                        )}
                      </ContextMenuContent>
                    </ContextMenu>
                  ))}
                </tbody>
              </table>
              </div>
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => { const val = parseInt(e.target.value, 10); setPageSize(val); localStorage.setItem('docPageSize', val); }}
                    className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white"
                    data-testid="page-size-select"
                  >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={150}>150</option>
                    <option value={200}>200</option>
                    <option value={400}>400</option>
                  </select>
                  <span className="text-sm text-gray-600">per page</span>
                </div>
                <div className="text-sm text-gray-600">
                  Showing <span className="font-medium">{sortedDocuments.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}</span>-<span className="font-medium">{Math.min(currentPage * pageSize, sortedDocuments.length)}</span> of <span className="font-medium">{sortedDocuments.length}</span> documents
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(1)} data-testid="page-first">First</Button>
                  <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} data-testid="page-prev">Prev</Button>
                  <span className="px-3 py-1 text-sm font-medium">{currentPage} / {totalPages || 1}</span>
                  <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} data-testid="page-next">Next</Button>
                  <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)} data-testid="page-last">Last</Button>
                </div>
              </div>
            </div>
            </>
          )}
        </div>
      </div>

      {/* Edit Document Dialog */}
      <Dialog open={editDocOpen} onOpenChange={setEditDocOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Document Information</DialogTitle>
            <DialogDescription>
              Update the document metadata. The file itself cannot be changed.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Document Number</Label>
              <Input
                value={editDocData.document_number || ''}
                onChange={(e) => setEditDocData({...editDocData, document_number: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={editDocData.title || ''}
                onChange={(e) => setEditDocData({...editDocData, title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Revision</Label>
              <Input
                value={editDocData.revision || ''}
                onChange={(e) => setEditDocData({...editDocData, revision: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Revision Date</Label>
              <Input
                type="date"
                value={editDocData.revision_date || ''}
                onChange={(e) => setEditDocData({...editDocData, revision_date: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Discipline</Label>
              <Input
                value={editDocData.discipline || ''}
                onChange={(e) => setEditDocData({...editDocData, discipline: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Discipline Code</Label>
              <Input
                value={editDocData.discipline_code || ''}
                onChange={(e) => setEditDocData({...editDocData, discipline_code: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Input
                value={editDocData.document_type || ''}
                onChange={(e) => setEditDocData({...editDocData, document_type: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Sub Type</Label>
              <Input
                value={editDocData.sub_type || ''}
                onChange={(e) => setEditDocData({...editDocData, sub_type: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Classification</Label>
              <Input
                value={editDocData.classification || ''}
                onChange={(e) => setEditDocData({...editDocData, classification: e.target.value})}
                placeholder="e.g., IFR, IFA, IFC"
                list="classification-edit-options"
              />
              <datalist id="classification-edit-options">
                {CLASSIFICATION_OPTIONS.map(opt => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>
            </div>
            <div className="space-y-2">
              <Label>Project Code</Label>
              <Input
                value={editDocData.project_code || ''}
                onChange={(e) => setEditDocData({...editDocData, project_code: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Project Title</Label>
              <Input
                value={editDocData.project_title || ''}
                onChange={(e) => setEditDocData({...editDocData, project_title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Company Code</Label>
              <Input
                value={editDocData.company_code || ''}
                onChange={(e) => setEditDocData({...editDocData, company_code: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Source Company</Label>
              <Input
                value={editDocData.source_company || ''}
                onChange={(e) => setEditDocData({...editDocData, source_company: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <select
                className="w-full h-9 rounded-md border px-3 py-1 text-sm"
                value={editDocData.priority || ''}
                onChange={(e) => setEditDocData({...editDocData, priority: e.target.value})}
              >
                <option value="">Select Priority</option>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Confidentiality</Label>
              <select
                className="w-full h-9 rounded-md border px-3 py-1 text-sm"
                value={editDocData.confidentiality || ''}
                onChange={(e) => setEditDocData({...editDocData, confidentiality: e.target.value})}
              >
                <option value="">Select Level</option>
                <option value="public">Public</option>
                <option value="internal">Internal</option>
                <option value="confidential">Confidential</option>
                <option value="restricted">Restricted</option>
              </select>
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Description</Label>
              <Input
                value={editDocData.description || ''}
                onChange={(e) => setEditDocData({...editDocData, description: e.target.value})}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Notes</Label>
              <Input
                value={editDocData.notes || ''}
                onChange={(e) => setEditDocData({...editDocData, notes: e.target.value})}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Tags (comma-separated)</Label>
              <Input
                value={editDocData.tags || ''}
                onChange={(e) => setEditDocData({...editDocData, tags: e.target.value})}
                placeholder="tag1, tag2, tag3"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditDocOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveDocEdit}>
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Transfer Document Dialog (System Admin only) */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer Document to Project</DialogTitle>
            <DialogDescription>
              Transfer &ldquo;{documentToTransfer?.document_number || documentToTransfer?.name}&rdquo; to a different project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Current Project</Label>
              <Input 
                value={projects.find(p => p.id === documentToTransfer?.project_id)?.name || 'No Project'} 
                disabled 
                className="bg-gray-100"
              />
            </div>
            <div>
              <Label>Target Project *</Label>
              <select
                value={targetProjectId}
                onChange={(e) => setTargetProjectId(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm"
              >
                <option value="">Select a project...</option>
                {projects.filter(p => p.id !== documentToTransfer?.project_id).map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setTransferDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleTransferDocument} disabled={!targetProjectId}>
              Transfer Document
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Multi-Document Full Editor */}

      {/* Create Incoming Transmittal Dialog */}
      <Dialog open={incomingTransmittalOpen} onOpenChange={setIncomingTransmittalOpen}>
        <DialogContent className="max-w-md" data-testid="incoming-transmittal-dialog">
          <DialogHeader>
            <DialogTitle>Create Incoming Transmittal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Transmittal Number *</Label>
              <Input
                data-testid="incoming-transmittal-nr"
                value={incomingTransmittalData.transmittal_nr}
                onChange={(e) => setIncomingTransmittalData({...incomingTransmittalData, transmittal_nr: e.target.value})}
                placeholder="e.g., INC-2025-0001"
              />
            </div>
            <div>
              <Label>Sent Date</Label>
              <Input
                data-testid="incoming-transmittal-date"
                type="date"
                value={incomingTransmittalData.sent_date}
                onChange={(e) => setIncomingTransmittalData({...incomingTransmittalData, sent_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Company (Received From)</Label>
              <Input
                data-testid="incoming-transmittal-company"
                value={incomingTransmittalData.company}
                onChange={(e) => setIncomingTransmittalData({...incomingTransmittalData, company: e.target.value})}
                placeholder="e.g., Enerflex"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              Linking {selectedDocuments.size} document(s) to this transmittal.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setIncomingTransmittalOpen(false)}>Cancel</Button>
              <Button data-testid="incoming-transmittal-submit" onClick={handleCreateIncomingTransmittal}>Create Transmittal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Outgoing Transmittal Dialog */}
      <Dialog open={transmittalOpen} onOpenChange={setTransmittalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Outgoing Transmittal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Transmittal Number *</Label>
                <Input
                  value={transmittalData.transmittal_nr}
                  onChange={(e) => setTransmittalData({...transmittalData, transmittal_nr: e.target.value})}
                  placeholder="e.g., MIR-ENER-2025-0017"
                />
              </div>
              <div>
                <Label>Attn (To Company)</Label>
                <Input
                  value={transmittalData.attn}
                  onChange={(e) => setTransmittalData({...transmittalData, attn: e.target.value})}
                  placeholder="e.g., Enerflex"
                />
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Purpose of Transmittal</Label>
              <div className="flex flex-wrap gap-3">
                {['For Information', 'For Review', 'For Approval', 'For Construction', 'As-Built', 'Other'].map(p => (
                  <label key={p} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={transmittalData.purpose.includes(p)}
                      onChange={(e) => {
                        const newPurpose = e.target.checked
                          ? [...transmittalData.purpose, p]
                          : transmittalData.purpose.filter(x => x !== p);
                        setTransmittalData({...transmittalData, purpose: newPurpose});
                      }}
                      className="rounded"
                    />
                    {p}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Documents ({selectedDocuments.size})</Label>
              <div className="border rounded-md overflow-auto max-h-64">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">No.</th>
                      <th className="px-3 py-2 text-left font-medium">Document Number</th>
                      <th className="px-3 py-2 text-left font-medium">Title</th>
                      <th className="px-3 py-2 text-left font-medium">Rev</th>
                      <th className="px-3 py-2 text-left font-medium">Class.</th>
                      <th className="px-3 py-2 text-left font-medium">Return Code</th>
                      <th className="px-3 py-2 text-left font-medium">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(selectedDocuments).map((docId, idx) => {
                      const doc = documents.find(d => d.id === docId) || { id: docId };
                      return (
                        <tr key={docId} className="border-t" data-testid={`transmittal-doc-row-${docId}`}>
                          <td className="px-3 py-1.5">{idx + 1}</td>
                          <td className="px-3 py-1.5 font-mono text-xs">{doc.document_number || doc.name || docId}</td>
                          <td className="px-3 py-1.5">{doc.title || '-'}</td>
                          <td className="px-3 py-1.5">{doc.latest_revision || doc.revision || '-'}</td>
                          <td className="px-3 py-1.5">{doc.classification || '-'}</td>
                          <td className="px-3 py-1.5">
                            <select
                              className="border rounded px-1 py-0.5 text-xs w-full"
                              value={transmittalData.return_codes[docId] || ''}
                              onChange={(e) => setTransmittalData({
                                ...transmittalData,
                                return_codes: {...transmittalData.return_codes, [docId]: e.target.value}
                              })}
                            >
                              <option value="">-</option>
                              <option value="Code 1">Code 1</option>
                              <option value="Code 2">Code 2</option>
                              <option value="Code 3">Code 3</option>
                              <option value="Code 4">Code 4</option>
                            </select>
                          </td>
                          <td className="px-3 py-1.5">
                            <Input
                              className="h-6 text-xs"
                              value={transmittalData.remarks[docId] || ''}
                              onChange={(e) => setTransmittalData({
                                ...transmittalData,
                                remarks: {...transmittalData.remarks, [docId]: e.target.value}
                              })}
                              placeholder="Remarks"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              <strong>Codes:</strong> Code 1: Approved | Code 2: Approved With Comment | Code 3: Rejected | Code 4: For Information Only
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setTransmittalOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateTransmittal}>Generate Transmittal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    
      {/* Create Transmittal Dialog */}
      <Dialog open={transmittalOpen} onOpenChange={setTransmittalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Outgoing Transmittal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Transmittal Number *</Label>
                <Input
                  value={transmittalData.transmittal_nr}
                  onChange={(e) => setTransmittalData({...transmittalData, transmittal_nr: e.target.value})}
                  placeholder="e.g., MIR-ENER-2025-0017"
                />
              </div>
              <div>
                <Label>Attn (To Company)</Label>
                <Input
                  value={transmittalData.attn}
                  onChange={(e) => setTransmittalData({...transmittalData, attn: e.target.value})}
                  placeholder="e.g., Enerflex"
                />
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Purpose of Transmittal</Label>
              <div className="flex flex-wrap gap-3">
                {['For Information', 'For Review', 'For Approval', 'For Construction', 'As-Built', 'Other'].map(p => (
                  <label key={p} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={transmittalData.purpose.includes(p)}
                      onChange={(e) => {
                        const newPurpose = e.target.checked
                          ? [...transmittalData.purpose, p]
                          : transmittalData.purpose.filter(x => x !== p);
                        setTransmittalData({...transmittalData, purpose: newPurpose});
                      }}
                      className="rounded"
                    />
                    {p}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Documents ({selectedDocuments.size})</Label>
              <div className="border rounded-md overflow-auto max-h-64">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">No.</th>
                      <th className="px-3 py-2 text-left font-medium">Document Number</th>
                      <th className="px-3 py-2 text-left font-medium">Title</th>
                      <th className="px-3 py-2 text-left font-medium">Rev</th>
                      <th className="px-3 py-2 text-left font-medium">Class.</th>
                      <th className="px-3 py-2 text-left font-medium">Return Code</th>
                      <th className="px-3 py-2 text-left font-medium">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(selectedDocuments).map((docId, idx) => {
                      const doc = documents.find(d => d.id === docId) || { id: docId };
                      return (
                        <tr key={docId} className="border-t" data-testid={`transmittal-doc-row-${docId}`}>
                          <td className="px-3 py-1.5">{idx + 1}</td>
                          <td className="px-3 py-1.5 font-mono text-xs">{doc.document_number || doc.name || docId}</td>
                          <td className="px-3 py-1.5">{doc.title || '-'}</td>
                          <td className="px-3 py-1.5">{doc.latest_revision || doc.revision || '-'}</td>
                          <td className="px-3 py-1.5">{doc.classification || '-'}</td>
                          <td className="px-3 py-1.5">
                            <select
                              className="border rounded px-1 py-0.5 text-xs w-full"
                              value={transmittalData.return_codes[docId] || ''}
                              onChange={(e) => setTransmittalData({
                                ...transmittalData,
                                return_codes: {...transmittalData.return_codes, [docId]: e.target.value}
                              })}
                            >
                              <option value="">-</option>
                              <option value="Code 1">Code 1</option>
                              <option value="Code 2">Code 2</option>
                              <option value="Code 3">Code 3</option>
                              <option value="Code 4">Code 4</option>
                            </select>
                          </td>
                          <td className="px-3 py-1.5">
                            <Input
                              className="h-6 text-xs"
                              value={transmittalData.remarks[docId] || ''}
                              onChange={(e) => setTransmittalData({
                                ...transmittalData,
                                remarks: {...transmittalData.remarks, [docId]: e.target.value}
                              })}
                              placeholder="Remarks"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              <strong>Codes:</strong> Code 1: Approved | Code 2: Approved With Comment | Code 3: Rejected | Code 4: For Information Only
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setTransmittalOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateTransmittal}>Generate Transmittal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Multi-Search Dialog (Patch v35) */}
      <Dialog open={multiSearchOpen} onOpenChange={setMultiSearchOpen}>
        <DialogContent className="max-w-2xl" data-testid="multi-search-dialog">
          <DialogHeader>
            <DialogTitle>Multi-Document Number Search</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Paste document numbers separated by comma, semicolon, newline or space.
              Search returns matches across ALL projects.
            </p>
            <Textarea
              rows={8}
              value={multiSearchInput}
              onChange={(e) => setMultiSearchInput(e.target.value)}
              placeholder={"MR-EPF-000-PL-DWG-0435\nMR-EPF-083-IC-DTS-0136, MR-EPF-000-IC-DTS-0130"}
              className="font-mono text-xs"
              data-testid="multi-search-input"
            />
            {multiSearchResult && (
              <div className="text-sm bg-blue-50 border border-blue-200 rounded p-2">
                Requested: <b>{multiSearchResult.requested}</b> ·
                Matched: <b className="text-green-700">{multiSearchResult.matched}</b> ·
                Missing: <b className="text-red-700">{multiSearchResult.missing?.length || 0}</b>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMultiSearchOpen(false); setMultiSearchResult(null); }}>Close</Button>
            <Button onClick={runMultiSearch} disabled={multiSearchLoading} data-testid="multi-search-run">
              {multiSearchLoading ? 'Searching…' : 'Search'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Transfer to Project Dialog (Patch v35) */}
      <Dialog open={bulkTransferOpen} onOpenChange={setBulkTransferOpen}>
        <DialogContent data-testid="bulk-transfer-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Transfer to Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Transfer <b>{selectedDocuments.size}</b> selected document(s) to a different project.
            </p>
            <div>
              <Label>Target Project</Label>
              <select
                className="w-full border rounded px-2 py-1.5 text-sm"
                value={bulkTransferProjectId}
                onChange={(e) => setBulkTransferProjectId(e.target.value)}
                data-testid="bulk-transfer-project-select"
              >
                <option value="">-- Select project --</option>
                {(projects || []).map((p) => (
                  <option key={p.id} value={p.id}>{p.name || p.code || p.id}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkTransferOpen(false)}>Cancel</Button>
            <Button onClick={runBulkTransfer} disabled={bulkTransferSubmitting || !bulkTransferProjectId} data-testid="bulk-transfer-run">
              {bulkTransferSubmitting ? 'Transferring…' : 'Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

// v51: keeps a runtime error in the table from blanking the whole page.
class DocumentsErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('Documents page crashed:', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex h-full items-center justify-center p-8" data-testid="documents-error-boundary">
          <div className="w-full max-w-md bg-white border border-red-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">This page hit an error</h2>
            <p className="text-sm text-gray-600 mb-4">
              The document list could not be displayed. Your documents are safe &mdash; nothing was changed.
              Reloading usually fixes it. If it keeps happening, send this message to your administrator.
            </p>
            <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 mb-4 max-h-32 overflow-auto text-gray-700 whitespace-pre-wrap">
              {String((this.state.error && this.state.error.message) || this.state.error)}
            </pre>
            <div className="flex gap-2">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                data-testid="documents-error-reload"
              >
                Reload page
              </button>
              <button
                onClick={() => this.setState({ error: null })}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                data-testid="documents-error-retry"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const DocumentsWithBoundary = (props) => (
  <DocumentsErrorBoundary>
    <Documents {...props} />
  </DocumentsErrorBoundary>
);

export default DocumentsWithBoundary;
